import React, { useState, useEffect, useContext } from 'react';
import classNames from 'classnames';
import { formatClassName, humanizeNumber } from "./chart-base";
import CountWidget from './count-widget';
import TweenedNumber from 'core/ui/tweened-number';
import LabelWithTooltip from './label-with-tooltip';
import MatchSelectByFile from './sub-components/match-select-by-file';
import MatchSelectGlobal from './sub-components/match-select';
import isEqual from 'lodash.isequal';
import ClearSelectsModal from './sub-components/clear-selects-modal';
import SessionContext from 'SessionContext';

const matchLevels = [
  {
    icon: "bi bi-person-fill",
    label: "Individual",
    value: "individual"
  },
  {
    icon: "bi bi-people-fill",
    label: "Household",
    value: "household"
  },
  {
    icon: "bi bi-house-door-fill",
    label: "Address",
    value: "address"
  },
  {
    icon: "bi bi-file-earmark-diff-fill",
    label: "By Selection",
    value: "file"
  }
];

const personTypeMatchLevels = [
  {
    icon: "bi bi-file-earmark-diff-fill",
    label: "By Selection",
    value: "file"
  }
];

const CountMatchWidget = (props) => {
  const context = useContext(SessionContext);
  const gMatch = props.filters?.find(f => f.all_match_value)?.all_match_value;

  const [selectionMatchingLevel, setSelectionMatchingLevel] = useState(gMatch || (props.chart_id === 'persontype' ? 'file' : null));
  const [selectionMatchByFile, setSelectionMatchByFile] = useState(true);
  const [showClearSelections, setShowClearSelections] = useState(false);
  const [newMatchLevel, setNewMatchLevel] = useState(null);
  const [lastFilterValues, setLastFilterValues] = useState([]);
  const [specialCaseLabels, setSpecialCaseLabels] = useState({});
  const [updatedLabelList, setUpdatedLabelList] = useState(null);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (!props.loaded) return;
    if (!isEqual(props.filters, lastFilterValues) && props.filters.length) {
      const level = newMatchLevel || selectionMatchingLevel || restoreMatchLevel();
      if (!level || level === selectionMatchingLevel) return;
      setSelectionMatchingLevel(level);
    }
    // Special case labels logic
    const config = props.config;
    const labels = config.value_labels;
    const viewOnly = props.viewOnly;
    let isSpecialCaseLabelsSet = specialCaseLabels ? Object.keys(specialCaseLabels) : [];
    let prefix = context.crossfilterStore.geoPrefix;
    let is_state = `${prefix}state`;
    if (!viewOnly &&
      !isSpecialCaseLabelsSet.length &&
      !(config.id === is_state) &&
      labels &&
      Object.keys(labels).length > 0) {
      setSpecialCaseLabels(labels);
    }
    // eslint-disable-next-line
  }, [props.filters, props.expandedView, props.loaded]);

  function restoreMatchLevel() {
    if (!props.loaded) return;
    const _filters = props.filters;
    let levels = [];
    _filters?.forEach(item => {
      levels.push(item.match_value || gMatch);
    });
    const same = levels.every((val, i, arr) => {
      return val === arr[0];
    });
    if (same && gMatch !== 'file') {
      return gMatch || (props.chart_id === 'persontype' ? 'file' : matchLevels[0].value);
    } else {
      return 'file';
    }
  }

  function getSelectionMatchIcon(level) {
    if (!props.loaded) return;
    let icon = "";
    const effectiveLevel = (props.chart_id === 'persontype' && level === 'file') ? 'individual' : level;
    if (matchLevels && matchLevels.length > 0) {
      const foundIcon = matchLevels.find(item => item.value === effectiveLevel);
      icon = (foundIcon && foundIcon.hasOwnProperty('icon')) ? foundIcon.icon : "";
    }
    return icon;
  }

  function handleLoseByFileSelections(match_value) {
    if( (match_value !== selectionMatchingLevel) && (selectionMatchingLevel !== "file") ) {
      setSelectionMatchingLevel(match_value);
    }
    setNewMatchLevel(match_value);
    if (!props.loaded || !match_value) return;
    if (match_value !== 'file' && selectionMatchingLevel === 'file') {
      setShowClearSelections(true);
      setLastFilterValues(props.filters);
      setNewMatchLevel(match_value);
    } else {
      handleGlobalMatchSelectionChange(match_value);
      setSelectionMatchByFile(false);
    }
  }

  function handleGlobalMatchSelectionChange(value, clearSelections = false) {
    setShowClearSelections(false);
    setLastFilterValues([]);
    const match_value = (!clearSelections) ? value : newMatchLevel || selectionMatchingLevel;
    if (!match_value) return;
    let filters = props.filters;
    setSelectionMatchingLevel(match_value);
    setSelectionMatchByFile(match_value === 'file' && filters.length > 0);
    if (!props.loaded || !filters || !filters.length) return;
    let filterUpdates = [];
    filters.forEach((filter) => {
      const filterMatchLevel = match_value !== "file" ? match_value : filter.match_value || matchLevels[0].value;
      filterUpdates.push({ ...filter, all_match_value: match_value, match_value: filterMatchLevel });
    });
    context.crossfilterStore.set_filters(props.config.id, filterUpdates);
    context.crossfilterStore.runCountRefresh();
    setNewMatchLevel(null);
    setLastFilterValues(filterUpdates);
    restoreMatchLevel();
  }

  function handleFileMatchSelectionChange(match_value, filter) {
    if (match_value === 'file') {
      if (!selectionMatchByFile) {
        return;
      } else {
        match_value = filter.match_level || matchLevels[0].value;
      }
    }
    let _filter = props.filters.filter(item => item.value === filter.id)[0];
    context.crossfilterStore.add_filter(
      props.config.id,
      _filter.series,
      _filter.value,
      _filter.is_negated,
      { all_match_value: selectionMatchingLevel, match_value: match_value }
    );
    context.crossfilterStore.runCountRefresh();
  }

  function clearSelectionsModal() {
    return (
      <ClearSelectsModal
        toggle={() => setShowClearSelections(!showClearSelections)}
        isOpen={showClearSelections}
        handleConfirm={handleGlobalMatchSelectionChange}
        value={selectionMatchingLevel}
        key={"clearselections-modal" + props.config.id}
      />
    );
  }

  function renderMatchDropdown(viewOnly, showGlobalMatch, showByFile, matchLevelsNoByFile, loaded) {
    let match_levels = !showByFile ? matchLevelsNoByFile : matchLevels;

    if(!viewOnly && props?.chart_id === "persontype") {
      match_levels = personTypeMatchLevels;
    }

    return !viewOnly && showGlobalMatch && (
      <MatchSelectGlobal
        matchLevels={match_levels}
        showByFile={showByFile}
        selectionMatching={selectionMatchingLevel}
        handleChange={handleLoseByFileSelections}
        loaded={loaded}
        key={'match-level-global' + selectionMatchingLevel}
      />
    );
  }

  // Chart rendering logic
  function renderChart(chartProps) {
    const { base, ...props } = chartProps;
    if (Object.keys(props.values).length === 0) { return }
    const config = props.config;
    const filters = props.filters || [];
    const viewOnly = props.viewOnly;
    const shouldTween = props.tween;
    const value_series = config.ds + '.' + config.id;
    const values = props.values[value_series] || {};
    let chartIsNegated = 0;
    if (filters.length > 0) {
      filters.forEach(filter => {
        if (filter.is_negated) {
          chartIsNegated = 1;
        }
      });
    }
    if (chartIsNegated) {
      filters.forEach(filter => filter.is_negated = 1);
    }
    let labels = config.value_labels;
    if (!labels || Object.keys(labels).length === 0) {
      labels = {};
      Object.entries(values).forEach(([val, _]) => labels[val] = val);
    }
    const isState = props.config.id === "state";
    let sortedList = base.sortListOutput(labels, values, filters);
    let updatedLabelList = base.updatedLabelList;
    if (updatedLabelList && base.searchText.length) {
      sortedList = base.sortListOutput(updatedLabelList, values, filters);
    }
    if (updatedLabelList && !Object.keys(updatedLabelList).length) {
      return <div className="flex-row">No Values Found</div>;
    }

    // widget types with match level
    const widgetsWithMatch = ["counts-match", "person-type"];
    const widgetsWithByFile = ["counts-match", "person-type"];
    let ui_id = config.uiType.toString().trim();
    let showByFile = widgetsWithByFile.includes(ui_id);
    const showGlobalMatch = widgetsWithMatch.includes(ui_id);

    if(viewOnly) {
      showByFile = false;
    }

    let effectiveShowByFile = showByFile;
    if (base.props?.chart_id === 'persontype' && !viewOnly) {
      effectiveShowByFile = selectionMatchingLevel === 'file';
    }

    // remove 'By Selection' from list if only a global match widget type.
    let matchLevelsNoByFile = [...matchLevels];
    const fileIndex = matchLevelsNoByFile.findIndex(level => level.value === "file");
    if (fileIndex !== -1) {
      matchLevelsNoByFile.splice(fileIndex, 1);
    }

    if (!sortedList) { return; }

    let matchDropdown = renderMatchDropdown(viewOnly, showGlobalMatch, showByFile, matchLevelsNoByFile, props.loaded);

    const filterOutput = sortedList.map(item => {
      const filterEntry = item.filtered;
      const isFiltered = filterEntry && !viewOnly;
      const isNegated = isFiltered && filterEntry.is_negated;
      const value = item.value || 0;
      const id = item.id;
      let label = item.label;
      let description = "";
      let date_uploaded = "";

      let is_upload = base?.props?.chart_id?.startsWith("up_");
      
      if(is_upload) {
        let _data = context.chartsStore.uploadsExtraData[base?.props?.chart_id][item.id];
        description = _data.description || "";
        date_uploaded = _data.date_uploaded || "";
      } 

      return (
        <div className={`flex-row ${!filterEntry ? "unfiltered" : ""} matching`} key={'item' + id}>
          {filterEntry && !effectiveShowByFile &&
            <i className={`${getSelectionMatchIcon(item.match_level || selectionMatchingLevel)} ${filterEntry ? "filtered" : " "}`}></i>
          }
          {filterEntry && effectiveShowByFile && showGlobalMatch &&
            <MatchSelectByFile
              className={`${getSelectionMatchIcon(item.match_level || selectionMatchingLevel)} ${filterEntry ? "filtered" : ""}`}
              matchLevels={matchLevelsNoByFile}
              thisFilter={item}
              filtered={filterEntry}
              showByFile={effectiveShowByFile}
              loaded={props.loaded}
              chartMatchLevel={selectionMatchingLevel}
              selectionMatchingLevel={item.match_level || selectionMatchingLevel}
              handleChange={handleFileMatchSelectionChange}
              key={'match-level-' + selectionMatchingLevel}
            />
          }
          {!viewOnly && isState && (
            <span onClick={() => base.handleFlyToState(label)}>
              <i className="fa-solid fa-crosshairs" />
              &nbsp;
            </span>
          )}
          <div
            className={classNames(
              "value",
              `${formatClassName(label)}-count`,
              {
                selected: isFiltered,
                not: isNegated || chartIsNegated,
              }
            )}
            onClick={() => base.onFilterClick(!isFiltered, config, value_series, id, chartIsNegated)}
          >
            <LabelWithTooltip 
              label={label} 
              description={description}
              dateUploaded={date_uploaded}
              parentProps={props} 
              cssClass="inner" 
            />
            <i className="spacer"></i>
            {shouldTween && <TweenedNumber className="count" value={value} />}
            {!shouldTween && (
              <span className="count">{humanizeNumber(value)}</span>
            )}
          </div>
        </div>
      );
    });
    return [matchDropdown, filterOutput, clearSelectionsModal()];
  }

  // Pass custom renderChart to CountWidgetF
  return (
    <CountWidget
      {...props}
      renderChart={renderChart}
      searchText={searchText}
      setSearchText={setSearchText}
      updatedLabelList={updatedLabelList}
      setUpdatedLabelList={setUpdatedLabelList}
    />
  );
};

export default CountMatchWidget;

